import { httpClient } from "../../../../infrastructure/api/http-client";
import { NEW_API_ENDPOINTS } from "../../../../shared/constants/api";
import type {
  InvoiceAdjustment,
  InvoiceAdjustmentSummary,
  InvoiceIssueResult,
  InvoiceItem,
  InvoiceModifyCode,
  InvoiceStatus,
  OrderRefundSummary,
  TaxDocumentBuyer,
  TaxDocumentConnection,
} from "../../../../domain/types/tax-document";

const ep = NEW_API_ENDPOINTS.companyInvoiceController;

type Wrapped<T> = { data: T };

/** 세금계산서/현금영수증 관리 API (CompanyInvoiceController) */
export const taxDocumentApi = {
  connection: async (companyId: number) =>
    (await httpClient.get<Wrapped<TaxDocumentConnection>>(ep.GET_CONNECTION(companyId))).data.data,

  certificateUrl: async (companyId: number) =>
    (await httpClient.post<Wrapped<{ url: string }>>(ep.POST_CERTIFICATE_URL(companyId))).data.data.url,

  create: async (companyId: number, orderNumber: string, buyer: TaxDocumentBuyer & { memo?: string | null }) =>
    (await httpClient.post<Wrapped<InvoiceItem>>(ep.POST(companyId), { orderNumber, buyer })).data.data,

  get: async (companyId: number, id: number) =>
    (await httpClient.get<Wrapped<InvoiceItem>>(ep.GET_ONE(companyId, id))).data.data,

  updateBuyer: async (companyId: number, id: number, buyer: TaxDocumentBuyer & { memo?: string | null }) =>
    (await httpClient.put<Wrapped<InvoiceItem>>(ep.PUT(companyId, id), buyer)).data.data,

  updateStatus: async (companyId: number, id: number, status: InvoiceStatus, memo: string | null) =>
    (await httpClient.put<Wrapped<InvoiceItem>>(ep.PUT_STATUS(companyId, id), { status, memo })).data.data,

  issue: async (companyId: number, id: number) =>
    (await httpClient.post<Wrapped<InvoiceItem>>(ep.POST_ISSUE(companyId, id))).data.data,

  batchIssue: async (companyId: number, ids: number[]) =>
    (await httpClient.put<Wrapped<InvoiceIssueResult[]>>(ep.PUT_BATCH_ISSUE(companyId), { ids })).data.data,

  cancel: async (companyId: number, id: number, reason: string | null) =>
    (await httpClient.post<Wrapped<InvoiceItem>>(ep.POST_CANCEL(companyId, id), { reason })).data.data,

  refresh: async (companyId: number, id: number) =>
    (await httpClient.post<Wrapped<InvoiceItem>>(ep.POST_REFRESH(companyId, id))).data.data,

  viewUrl: async (companyId: number, id: number) =>
    (await httpClient.get<Wrapped<{ url: string }>>(ep.GET_VIEW_URL(companyId, id))).data.data.url,

  adjustments: async (companyId: number, invoiceId: number) =>
    (await httpClient.get<Wrapped<InvoiceAdjustment[]>>(ep.GET_ADJUSTMENTS(companyId, invoiceId))).data.data,

  executeAdjustment: async (
    companyId: number,
    adjustmentId: number,
    body: { modifyCode?: InvoiceModifyCode; writeDate?: string } = {},
  ) =>
    (await httpClient.post<Wrapped<InvoiceAdjustment>>(ep.POST_ADJUSTMENT_EXECUTE(companyId, adjustmentId), body)).data
      .data,

  discardAdjustment: async (companyId: number, adjustmentId: number) =>
    (await httpClient.post<Wrapped<InvoiceAdjustment>>(ep.POST_ADJUSTMENT_DISCARD(companyId, adjustmentId))).data.data,

  adjustmentSummary: async (companyId: number) =>
    (await httpClient.get<Wrapped<InvoiceAdjustmentSummary>>(ep.GET_ADJUSTMENT_SUMMARY(companyId))).data.data,
};

const refundEp = NEW_API_ENDPOINTS.companyOrderRefundController;

/** 주문 부분 환불 기록 API. 돈은 시스템 밖에서 돌려주고, 여기서는 기록과 증빙 조정만 만든다 */
export const orderRefundApi = {
  summary: async (companyId: number, orderId: number) =>
    (await httpClient.get<Wrapped<OrderRefundSummary>>(refundEp.GET(companyId, orderId))).data.data,

  create: async (companyId: number, orderId: number, body: { amount: number; reason: string; refundedAt?: string }) =>
    (await httpClient.post<Wrapped<OrderRefundSummary>>(refundEp.POST(companyId, orderId), body)).data.data,
};

/**
 * 서버에서 받은 URL 을 새 창으로 연다.
 *
 * await 뒤에 window.open 을 부르면 팝업 차단에 걸린다. 클릭 순간에 빈 창을 먼저 열고,
 * URL 을 받으면 그 창의 주소를 바꾼다. 연동사 URL 은 60초만 유효하므로 받자마자 연다.
 */
export const openUrlFromServer = async (load: () => Promise<string>) => {
  const popup = window.open("about:blank", "_blank", "width=920,height=860");
  try {
    const url = await load();
    if (popup) popup.location.href = url;
    else window.open(url, "_blank");
  } catch (error) {
    popup?.close();
    throw error;
  }
};
